import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
} from 'react'
import {
  useSessionContext,
  config,
} from '@crunchyroll/cr-static-shared-components'
import { initWithSSO } from '@api/monterosa/initWithSSO'
import {
  answer as sdkAnswer,
  onElementUpdated,
  onElementResults,
  getElements,
} from '@monterosa/sdk-interact-kit'
import data from '@data/config'
import { readVotingLoginState, withVotingIdentity } from '@utils'
import { isVotingClosedAt } from './votingDeadline'

const VoteApiContext = createContext()
const useVoteApiContext = () => useContext(VoteApiContext)
const VoteApiConsumer = VoteApiContext.Consumer

const VoteApiProvider = ({ children, voteType: passedVoteType }) => {
  const { isAuthenticated, user } = useSessionContext()

  const isProfileComplete = (user) => {
    return !!(user && user.email && (user.etpGuid || user.id))
  }

  const { env } = config
  const isProduction = env === 'prod'
  const votingEnvironment = ['stg', 'staging'].includes(env)
    ? 'staging'
    : env || 'unknown'
  const votingSeason = 'AA27'

  const now = new Date()
  const votingEndTime = new Date('2026-04-16T06:59:00Z')

  // Temporary non-production validation deadline.
  const effectiveVotingEndTime = isProduction
    ? votingEndTime
    : new Date('2027-04-16T06:59:00Z')

  const isVotingClosed = isVotingClosedAt(now, effectiveVotingEndTime)

  const [votingClosed, setVotingClosed] = useState(isVotingClosed)
  const [voteType, setVoteType] = useState(passedVoteType || 'fan')
  const [shouldShowLogin, setShouldShowLogin] = useState(true)

  useEffect(() => {
    if (passedVoteType) return

    const inferVoteType = () => {
      const path = typeof window !== 'undefined' ? window.location.pathname : ''
      return path.includes('judge-vote') ? 'judge' : 'fan'
    }

    const detectedType = inferVoteType()
    setVoteType(detectedType)

    const checkVoteType = () => {
      const newType = inferVoteType()
      if (newType !== detectedType) {
        setVoteType(newType)
      }
    }

    if (typeof window !== 'undefined') {
      const timeoutId = setTimeout(checkVoteType, 500)

      return () => clearTimeout(timeoutId)
    }
  }, [passedVoteType])

  const isJudgeFlow = voteType === 'judge'

  const { monterosa } = data
  const { host } = monterosa

  const fansProjectId = isProduction
    ? monterosa.productionFanProjectId
    : monterosa.stagingFanProjectId
  const judgesProjectId = isProduction
    ? monterosa.productionJudgeProjectId
    : monterosa.stagingJudgeProjectId

  const [sdk, setSdk] = useState(null)
  const [formMeta, setFormMeta] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [resolvedLoginStateKey, setResolvedLoginStateKey] = useState(null)
  const [elements, setElements] = useState([])
  const [votedCount, setVotedCount] = useState(0)
  const [isDoneVoting, setIsDoneVoting] = useState(false)
  const [isRevote, setIsRevote] = useState('')
  const [accessCode, setAccessCode] = useState('')
  const [isJudge, setIsJudge] = useState(false)
  const [events, setEvents] = useState(null)
  const [activeEvent, setActiveEvent] = useState(null)
  const [oldEvent, setOldEvent] = useState(null)
  const [oldElements, setOldElements] = useState(null)
  const [prevVotes, setPrevVotes] = useState([])
  const [currentEventId, setCurrentEventId] = useState(null)
  const [hasProcessedOldElements, setHasProcessedOldElements] = useState(false)
  const [oldEventId, setOldEventId] = useState(null)
  const [currentForm, setCurrentForm] = useState(null)
  const [hasAutoSubmittedForm, setHasAutoSubmittedForm] = useState(false)
  const [isPrimaryProfile, setIsPrimaryProfile] = useState(null)
  const formServiceRef = useRef(null)

  const handleUpdate = (updatedElement) => {
    setElements((prev) =>
      prev.map((el) => (el.id === updatedElement.id ? updatedElement : el))
    )
  }

  const fetchForm = async (formService, id) => {
    if (!formService || !id || isVotingClosed) {
      console.warn('Skipping fetchForm — missing or invalid inputs.')
      return
    }

    const url = `${formService}/form/${id}.json`
    try {
      const response = await fetch(url)
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`)
      const data = await response.json()
      setCurrentForm(data)
    } catch (err) {
      console.error('fetchForm failed:', err)
      setError(err)
    }
  }

  useEffect(() => {
    if (isVotingClosed) return
    localStorage.setItem('voterType', voteType)
    setIsJudge(isJudgeFlow)
  }, [voteType, isJudgeFlow])

  useEffect(() => {
    if (!user) {
      setIsPrimaryProfile(null)
    } else if (isProfileComplete(user)) {
      setIsPrimaryProfile(user.profileId === user.etpGuid)
    } else {
      setIsPrimaryProfile(true)
    }
  }, [user?.profileId, user?.etpGuid, user])

  useEffect(() => {
    if (isVotingClosed) return

    const count = elements.reduce((acc, el) => {
      const answers = el?.userAnswer?._answers
      if (!answers) return acc
      const hasVoted = [...answers.values()].some((v) => v === 1)
      return hasVoted ? acc + 1 : acc
    }, 0)

    setVotedCount(count)
    setIsDoneVoting(count === 24)
  }, [elements, isJudgeFlow, isVotingClosed])

  useEffect(() => {
    if (isVotingClosed) {
      setIsLoading(false)
      return
    }

    if (!passedVoteType && typeof window !== 'undefined') {
      const path = window.location.pathname
      const detectedType = path.includes('judge-vote') ? 'judge' : 'fan'
      if (voteType !== detectedType) {
        return
      }
    }

    let cancelled = false
    let unsubscribeCurrent = null
    const initialize = async () => {
      try {
        const ssoAuthState = {
          isAuthenticated,
          user,
        }

        const targetProjectId = isJudgeFlow ? judgesProjectId : fansProjectId

        const {
          sdk: initializedSdk,
          project,
          activeEvent,
          events,
          elements: fetchedElements,
          unsubscribeOnEventAdded,
          oldEvent,
          oldElements,
          ssoAuth,
          rolloverResult: initialRolloverResult,
        } = await initWithSSO(
          host,
          targetProjectId,
          (newEvent, newElements, updatedOldData) => {
            if (cancelled) return
            const nextFormId = newEvent?.fields?.formId?.data?.[0]?.id
            setActiveEvent(newEvent)
            setFormMeta((prev) => ({
              ...prev,
              host,
              projectId: targetProjectId,
              season: votingSeason,
              environment: votingEnvironment,
              eventId: newEvent?.id,
              eventName: newEvent?.name,
              currentFormId: nextFormId,
            }))
            fetchForm(formServiceRef.current, nextFormId)
            setElements(newElements)
            setOldEvent(updatedOldData?.oldEvent || null)
            setOldElements(updatedOldData?.oldElements || [])
            setHasProcessedOldElements(false)
            setPrevVotes([])
            if (
              updatedOldData?.rolloverResult?.isEventChanged ||
              updatedOldData?.rolloverResult?.didCleanup
            ) {
              setIsLoggedIn(false)
              setHasAutoSubmittedForm(false)
              setIsRevote('')
            }
          },
          ssoAuthState,
          { season: votingSeason, environment: votingEnvironment }
        )
        if (cancelled) {
          unsubscribeOnEventAdded?.()
          return
        }
        unsubscribeCurrent = unsubscribeOnEventAdded

        setShouldShowLogin(ssoAuth.shouldShowLogin)
        setSdk(initializedSdk)
        setElements(fetchedElements)
        setEvents(events)
        setOldEvent(oldEvent)
        setOldElements(oldElements)
        setActiveEvent(activeEvent)

        const validElements = fetchedElements.filter((el) => el?.timeline)
        validElements.forEach((el) => {
          onElementUpdated(el, handleUpdate)
          onElementResults(el, handleUpdate)
        })

        if (currentEventId !== activeEvent?.id) {
          setCurrentEventId(activeEvent?.id)
          setHasProcessedOldElements(false)
        }

        if (oldEventId !== oldEvent?.id) {
          setOldEventId(oldEvent?.id)
          setHasProcessedOldElements(false)
        }

        const formService = project.extensions?.submissions?.find(
          (item) => item.name === 'webservice'
        )?.data
        if (!formService) throw new Error('Webservice URL not found')
        formServiceRef.current = formService

        const currentFormId = activeEvent?.fields?.formId?.data?.[0]?.id
        const eventId = activeEvent?.id
        const eventName = activeEvent?.name

        // Cleanup must never run a second time after SDK hydration.
        const rolloverResult = initialRolloverResult || {
          isEventChanged: false,
          didCleanup: false,
        }

        fetchForm(formService, currentFormId)

        if (rolloverResult.isEventChanged || rolloverResult.didCleanup) {
          setIsLoggedIn(false)
        }
        if (rolloverResult.isEventChanged || rolloverResult.didCleanup) {
          setHasAutoSubmittedForm(false)
          setPrevVotes([])
          setIsRevote('')
        }

        setFormMeta({
          formService,
          currentFormId,
          eventId,
          eventName,
          projectId: targetProjectId,
          host,
          season: votingSeason,
          environment: votingEnvironment,
        })
      } catch (err) {
        console.error('Initialization failed:', err)
        if (!cancelled) setError(err)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    initialize()
    return () => {
      cancelled = true
      unsubscribeCurrent?.()
    }
  }, [
    isJudgeFlow,
    host,
    judgesProjectId,
    fansProjectId,
    isVotingClosed,
    voteType,
    votingEnvironment,
  ])

  const currentLoginStateKey =
    formMeta?.eventId && formMeta?.currentFormId
      ? `form-${formMeta.eventId}-${formMeta.currentFormId}`
      : null
  const loginStateReady =
    Boolean(currentLoginStateKey) &&
    resolvedLoginStateKey === currentLoginStateKey

  useEffect(() => {
    if (!currentLoginStateKey) {
      setResolvedLoginStateKey(null)
      return
    }
    const hasCompletedForm = !!localStorage.getItem(currentLoginStateKey)
    setIsLoggedIn(hasCompletedForm)
    setResolvedLoginStateKey(currentLoginStateKey)
  }, [currentLoginStateKey, formMeta, isAuthenticated, user])

  useEffect(() => {
    setShouldShowLogin(!isLoggedIn)
  }, [isLoggedIn])

  useEffect(() => {
    const loginFormState = readVotingLoginState(formMeta)
    const prevVotesReady = oldElements !== null && prevVotes !== undefined
    const hasPrevVotes = prevVotes && prevVotes.length > 0

    if (
      isAuthenticated &&
      user &&
      isProfileComplete(user) &&
      loginFormState &&
      !isLoggedIn &&
      formMeta &&
      currentForm &&
      prevVotesReady &&
      hasPrevVotes
    ) {
      const { eventId, currentFormId } = formMeta
      const key = `form-${eventId}-${currentFormId}`
      const globalSSOKey = `sso-terms-accepted-${user.email || user.etpGuid}`

      localStorage.setItem(
        key,
        JSON.stringify(
          withVotingIdentity(
            {
              isSubmitted: false,
              ssoLoggedIn: true,
              lastVoteDate: new Date().toISOString(),
            },
            formMeta
          )
        )
      )
      localStorage.setItem(globalSSOKey, 'true')
      setIsLoggedIn(true)
      localStorage.removeItem('loginFormState')
    }

    if (
      isAuthenticated &&
      user &&
      isProfileComplete(user) &&
      loginFormState &&
      !isLoggedIn &&
      formMeta &&
      currentForm &&
      prevVotesReady
    ) {
      const { eventId, currentFormId } = formMeta
      const key = `form-${eventId}-${currentFormId}`

      localStorage.setItem(
        key,
        JSON.stringify(
          withVotingIdentity(
            {
              isSubmitted: false,
              ssoReadyForSubmit: true,
              ssoUser: true,
              hasPreviousVotes: hasPrevVotes,
              lastVoteDate: new Date().toISOString(),
            },
            formMeta
          )
        )
      )
      localStorage.removeItem('loginFormState')
      return
    }
  }, [
    isAuthenticated,
    user,
    formMeta,
    isLoggedIn,
    currentForm,
    prevVotes,
    oldElements,
  ])

  useEffect(() => {
    if (!sdk) return
    // Recovery is only from a verified earlier event in this same project.
    if (
      !oldEvent?.id ||
      oldEvent.id === activeEvent?.id ||
      oldEvent.context?.project?.id !== formMeta?.projectId ||
      !oldElements?.length
    ) {
      if (prevVotes.length > 0) setPrevVotes([])
      return
    }
    const hasValidAuth = isAuthenticated && user?.id
    const hasProcessedForCurrentUser = hasProcessedOldElements && hasValidAuth

    if (hasProcessedForCurrentUser) return

    const lastVotes = oldElements.reduce((acc, el) => {
      const categoryId = el?.question?.categoryId
      const elementId = el?.id
      const userAnswer = el?.userAnswer

      if (!categoryId) return acc

      let selectedOption = null

      if (userAnswer) {
        if (
          userAnswer._answers &&
          typeof userAnswer._answers.entries === 'function'
        ) {
          const answersMap = userAnswer._answers
          selectedOption = [...(answersMap.entries() || [])].find(
            ([, v]) => v === 1
          )?.[0]
        }

        if (
          selectedOption === null &&
          userAnswer._answers &&
          typeof userAnswer._answers === 'object'
        ) {
          const answers = userAnswer._answers
          for (const [key, value] of Object.entries(answers)) {
            if (value === 1) {
              selectedOption = parseInt(key, 10)
              break
            }
          }
        }

        if (selectedOption === null && typeof userAnswer === 'object') {
          for (const [key, value] of Object.entries(userAnswer)) {
            if (value === 1 && !isNaN(key)) {
              selectedOption = parseInt(key, 10)
              break
            }
          }
        }
      }

      if (selectedOption === null && elementId) {
        const voteKey = `monterosa_sdk_element_${elementId}_vote`
        const storedVote = localStorage.getItem(voteKey)
        if (storedVote) {
          try {
            const voteData = JSON.parse(storedVote)
            const voteEntry = voteData?.[0]
            if (voteEntry?.value === 1) {
              selectedOption = voteEntry.option
            }
          } catch (e) {
            console.warn(
              '[VoteApiContext] Failed to parse localStorage vote:',
              voteKey,
              e
            )
          }
        }

        if (selectedOption === null) {
          for (const otherEl of oldElements) {
            if (otherEl?.question?.categoryId === categoryId && otherEl?.id) {
              const voteKey = `monterosa_sdk_element_${otherEl.id}_vote`
              const storedVote = localStorage.getItem(voteKey)
              if (storedVote) {
                try {
                  const voteData = JSON.parse(storedVote)
                  const voteEntry = voteData?.[0]
                  if (voteEntry?.value === 1) {
                    selectedOption = voteEntry.option
                    break
                  }
                } catch (e) {
                  // Skip invalid entries
                }
              }
            }
          }
        }
      }

      if (selectedOption !== null && selectedOption !== undefined) {
        acc.push({ categoryId, userAnswer: selectedOption })
      }
      return acc
    }, [])

    setPrevVotes(lastVotes)
    setHasProcessedOldElements(true)
  }, [
    sdk,
    oldElements,
    hasProcessedOldElements,
    isAuthenticated,
    user?.id,
    oldEvent?.id,
    activeEvent?.id,
    formMeta?.projectId,
    oldElements?.length,
  ])

  const contextAnswer = async (element, optionIndex) => {
    if (!sdk || !activeEvent) return
    try {
      await sdkAnswer(element, { option: optionIndex, value: 1 })
      const refreshed = await getElements(activeEvent)
      setElements(refreshed)
    } catch (err) {
      console.error('Vote failed:', err)
    }
  }

  const getUserAnswerForCategory = (categoryId) => {
    const el = elements.find((el) => el?.question?.categoryId === categoryId)
    const entries = el?.userAnswer?._answers?.entries?.() || []
    for (const [key, value] of entries) {
      if (value === 1) return key
    }
    return null
  }

  const getAllUserAnswers = () =>
    elements
      .map((el) => {
        const categoryId = el?.question?.categoryId
        const userAnswer = getUserAnswerForCategory(categoryId)
        return userAnswer !== null ? { categoryId, userAnswer } : null
      })
      .filter(Boolean)

  const hasUserVotedAnywhere = () =>
    elements.some((el) =>
      [...(el?.userAnswer?._answers?.values() || [])].some((v) => v === 1)
    )

  const state = {
    formMeta,
    isLoading,
    error,
    isLoggedIn,
    setIsLoggedIn,
    loginStateReady,
    contextAnswer,
    elements,
    getUserAnswerForCategory,
    getAllUserAnswers,
    hasUserVotedAnywhere,
    isRevote,
    setIsRevote,
    accessCode,
    setAccessCode,
    votedCount,
    setVotedCount,
    isDoneVoting,
    setIsDoneVoting,
    isJudge,
    oldEvent,
    oldElements,
    prevVotes,
    setPrevVotes,
    currentForm,
    setCurrentForm,
    activeEvent,
    setActiveEvent,
    voteType,
    votingClosed,
    setVotingClosed,
    shouldShowLogin,
    setShouldShowLogin,
    hasAutoSubmittedForm,
    isPrimaryProfile,
    setIsPrimaryProfile,
  }

  return (
    <VoteApiContext.Provider value={state}>{children}</VoteApiContext.Provider>
  )
}

export { VoteApiConsumer, VoteApiProvider, useVoteApiContext }
